import React from 'react';
import LocalAtmIcon from '@material-ui/icons/LocalAtm';
import ImageOutlinedIcon from '@material-ui/icons/ImageOutlined';
import { cashValueOf, formatCashValue } from '../utils/cashProduct';
import {
    Grid,
    Card,
    Typography,
    makeStyles,
    CardActionArea,
    Button
} from '@material-ui/core';

const MEDIA_HEIGHT = 148;

const useStyles = makeStyles(theme => ({
    card: {
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        transition: 'border-color 120ms ease, transform 120ms ease',
        '&:hover': {
            borderColor: theme.palette.primary.main,
            transform: 'translateY(-2px)',
        },
        '@media (prefers-reduced-motion: reduce)': {
            transition: 'none',
            '&:hover': { transform: 'none' },
        },
    },
    cardSoldOut: {
        '&:hover': { borderColor: theme.palette.divider, transform: 'none' },
    },
    actionArea: {
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        '&.Mui-focusVisible': {
            outline: `3px solid ${theme.palette.primary.dark}`,
            outlineOffset: -3,
        },
    },
    media: {
        position: 'relative',
        height: MEDIA_HEIGHT,
        backgroundColor: theme.palette.background.default,
        backgroundSize: 'contain',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        borderBottom: `1px solid ${theme.palette.divider}`,
    },
    mediaCash: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: theme.palette.primary.main,
    },
    mediaEmpty: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#B9C2B6',
    },
    cashIcon: {
        fontSize: 64,
        color: theme.palette.primary.dark,
    },
    emptyIcon: {
        fontSize: 48,
    },
    // Véu por cima da imagem em vez de opacity no container: o selo precisa
    // continuar legível, e opacity herdada apagava ele junto.
    soldOutVeil: {
        position: 'absolute',
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        backgroundColor: 'rgba(245, 247, 245, 0.62)',
    },
    soldOutTag: {
        position: 'absolute',
        zIndex: 1,
        top: 10,
        left: 10,
        padding: '2px 10px',
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 700,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        color: '#FFFFFF',
        backgroundColor: 'rgba(31, 42, 28, 0.82)',
    },
    faded: {
        opacity: 0.5,
    },
    content: {
        padding: '14px 16px 16px',
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
    },
    // width 100% é o que faz o corte virar reticências: com o conteúdo em
    // flex-column + align-items center, o Typography ganhava largura max-content
    // e o texto vazava pra fora do card em vez de truncar.
    title: {
        width: '100%',
        fontSize: 15,
        fontWeight: 600,
        lineHeight: 1.35,
        minHeight: 41,
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
    },
    cashTitle: {
        width: '100%',
        fontSize: 26,
        fontWeight: 700,
        lineHeight: 1.2,
        letterSpacing: '-0.02em',
        color: theme.palette.primary.dark,
    },
    cashName: {
        width: '100%',
        marginTop: 2,
        fontSize: 13,
        color: theme.palette.text.secondary,
        display: '-webkit-box',
        WebkitLineClamp: 1,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
    },
    footer: {
        marginTop: 'auto',
        paddingTop: 12,
    },
    price: {
        display: 'inline-block',
        padding: '4px 10px',
        borderRadius: 999,
        fontSize: 13,
        fontWeight: 700,
        color: theme.palette.primary.dark,
        backgroundColor: '#EAF7E6',
    },
    priceMuted: {
        color: theme.palette.text.secondary,
        backgroundColor: theme.palette.background.default,
    },
    place: {
        fontSize: 13,
        color: theme.palette.text.secondary,
    },
    giftButton: {
        margin: '0 16px 16px',
        width: 'calc(100% - 32px)',
    },
}));

/** Preço mínimo em pontos entre os pontos de troca. null quando não há nenhum. */
const lowestPrice = (places) => {
    const prices = places
        .map(place => Number(place.price))
        .filter(price => Number.isFinite(price));
    return prices.length ? Math.min(...prices) : null;
};

/**
 * Produto com controle de estoque zerado em TODOS os pontos de troca.
 * Só marca quando o payload realmente traz os campos — sem eles não dá pra
 * afirmar que está esgotado, e esconder produto disponível é pior que o inverso.
 */
const isSoldOut = (product, places) => {
    if (product.isGift || product.stock !== true) { return false; }

    const hasStockInfo = places.some(place =>
        Number.isFinite(Number(place.warehouse)) || Number.isFinite(Number(place.showcase)));
    if (!hasStockInfo) { return false; }

    return !places.some(place => Number(place.warehouse) > 0 || Number(place.showcase) > 0);
};

const ProductCard = ({ product, productSelectedCallback, generateCoupon }) => {
    const classes = useStyles();

    const places = Array.isArray(product.productPlaces) ? product.productPlaces : [];
    const soldOut = isSoldOut(product, places);
    const price = lowestPrice(places);

    // Produto em dinheiro: o valor de face é o título. Sem valor resolvível
    // (legado sem cashValue e com nome textual) cai no nome — antes ficava vazio.
    const cashLabel = product.isCash ? formatCashValue(product) : '';
    const name = product.name === undefined || product.name === null ? '' : String(product.name);
    // Só repete o nome embaixo do valor quando ele é nome de verdade: nos legados
    // o nome ERA o número, e mostrar "10" sob "R$ 10,00" é ruído.
    const showCashName = Boolean(cashLabel) && name.trim() !== '' && cashValueOf({ name }) === null;

    const clickable = Boolean(productSelectedCallback) && !soldOut;

    return (
        <Grid xs={12} sm={6} md={4} lg={3} xl={2} item>
            <Card className={`${classes.card} ${soldOut ? classes.cardSoldOut : ''}`}>
                <CardActionArea
                    className={classes.actionArea}
                    disabled={!clickable}
                    onClick={() => clickable && productSelectedCallback(product)}
                >
                    <div
                        className={[
                            classes.media,
                            product.isCash ? classes.mediaCash : '',
                            !product.isCash && !product.imagePath ? classes.mediaEmpty : '',
                        ].join(' ')}
                        style={!product.isCash && product.imagePath ? { backgroundImage: `url(${product.imagePath})` } : undefined}
                    >
                        {product.isCash && <LocalAtmIcon className={classes.cashIcon} />}
                        {!product.isCash && !product.imagePath && <ImageOutlinedIcon className={classes.emptyIcon} />}
                        {soldOut && <span className={classes.soldOutVeil} />}
                        {soldOut && <span className={classes.soldOutTag}>Esgotado</span>}
                    </div>

                    <div className={`${classes.content} ${soldOut ? classes.faded : ''}`}>
                        {cashLabel ? (
                            <>
                                <Typography component="h3" className={classes.cashTitle}>
                                    {cashLabel}
                                </Typography>
                                {showCashName && (
                                    <Typography component="p" className={classes.cashName} title={name}>
                                        {name}
                                    </Typography>
                                )}
                            </>
                        ) : (
                            <Typography component="h3" className={classes.title} title={name}>
                                {name}
                            </Typography>
                        )}

                        <div className={classes.footer}>
                            {product.isGift ? (
                                <span className={classes.place}>{product.place}</span>
                            ) : price === null ? (
                                <span className={`${classes.price} ${classes.priceMuted}`}>Sem ponto de troca</span>
                            ) : (
                                <span className={classes.price}>
                                    {places.length > 1 ? `A partir de ${price} pontos` : `${price} pontos`}
                                </span>
                            )}
                        </div>
                    </div>
                </CardActionArea>

                {product.isGift && (
                    <Button
                        variant="contained"
                        color="primary"
                        className={classes.giftButton}
                        onClick={generateCoupon}
                    >
                        Gerar cupom
                    </Button>
                )}
            </Card>
        </Grid>
    );
};

export default ProductCard;
